// GlobalDropdownField.tsx
import React from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  icon?: string;
}

interface GlobalDropdownFieldProps {
  label?: string;
  value: any;
  onChange: (value: any) => void;
  options: DropdownOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  icon?: string;
  fullWidth?: boolean;
  filter?: boolean;
  showClear?: boolean;
  optionLabel?: string;
  optionValue?: string;
}

export const GlobalDropdownField: React.FC<GlobalDropdownFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  required = false,
  disabled = false,
  error = '',
  icon,
  fullWidth = true,
  filter = false,
  showClear = false,
  optionLabel = 'label',
  optionValue = 'value',
}) => {
  const handleChange = (e: DropdownChangeEvent) => {
    onChange(e.value);
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 600,
          color: error ? '#ef4444' : '#1e3a8a',
          marginBottom: '8px'
        }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        {icon && (
          <i 
            className={icon} 
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8',
              fontSize: '16px',
              pointerEvents: 'none',
              zIndex: 1
            }}
          ></i>
        )}
        
        <Dropdown
          value={value}
          onChange={handleChange}
          options={options}
          optionLabel={optionLabel}
          optionValue={optionValue}
          placeholder={placeholder}
          disabled={disabled}
          filter={filter}
          showClear={showClear}
          style={{
            width: '100%',
            height: '44px',
            border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`,
            borderRadius: '10px',
            background: disabled ? '#f9fafb' : 'white',
          }}
          panelStyle={{
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            marginTop: '4px',
            border: '1px solid #e5e7eb'
          }}
          className={`global-dropdown ${icon ? 'has-icon' : ''}`}
        />
      </div>
      
      {error && (
        <span style={{
          display: 'block',
          marginTop: '6px',
          fontSize: '12px',
          color: '#ef4444',
          fontWeight: 500
        }}>
          {error}
        </span>
      )}

      <style>{`
        .global-dropdown.p-dropdown,
        .global-dropdown .p-dropdown {
          transition: all 0.2s ease;
        }

        .global-dropdown.p-dropdown .p-dropdown-label,
        .global-dropdown .p-dropdown .p-dropdown-label,
        .global-dropdown.p-dropdown span.p-dropdown-label,
        .global-dropdown .p-dropdown span.p-dropdown-label {
          padding-right: 55px !important;
          padding-left: 14px !important;
          font-size: 13px !important;
          display: block !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        .global-dropdown.p-dropdown .p-inputtext,
        .global-dropdown .p-dropdown .p-inputtext {
          padding-right: 55px !important;
          padding-left: 14px !important;
          font-size: 13px !important;
        }

        .global-dropdown.p-dropdown .p-dropdown-label.p-placeholder,
        .global-dropdown .p-dropdown .p-dropdown-label.p-placeholder {
          color: #9ca3af !important;
        }

        .global-dropdown.has-icon.p-dropdown span.p-dropdown-label,
        .global-dropdown.has-icon .p-dropdown span.p-dropdown-label,
        .global-dropdown.has-icon.p-dropdown .p-inputtext,
        .global-dropdown.has-icon .p-dropdown .p-inputtext {
          padding-left: 42px !important;
        }

        .global-dropdown.p-dropdown.p-disabled .p-inputtext,
        .global-dropdown.p-dropdown.p-disabled .p-dropdown-label,
        .global-dropdown .p-dropdown.p-disabled .p-inputtext,
        .global-dropdown .p-dropdown.p-disabled .p-dropdown-label {
          color: #9ca3af;
        }

        .global-dropdown.p-dropdown:not(.p-disabled):hover,
        .global-dropdown .p-dropdown:not(.p-disabled):hover {
          border-color: #3b82f6 !important;
        }

        .global-dropdown.p-dropdown:not(.p-disabled).p-focus,
        .global-dropdown .p-dropdown:not(.p-disabled).p-focus {
          outline: 0;
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }

        .global-dropdown.p-dropdown .p-dropdown-trigger,
        .global-dropdown .p-dropdown .p-dropdown-trigger {
          color: #94a3b8;
          width: 40px;
          flex-shrink: 0;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
          padding: 10px 14px;
          font-size: 13px;
          color: #1f2937;
          transition: all 0.15s ease;
          border-radius: 6px;
          margin: 2px 4px;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover {
          background: rgba(59, 130, 246, 0.08);
          color: #1e3a8a;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.08) 100%);
          color: #1e3a8a;
          font-weight: 600;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header .p-dropdown-filter {
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }

        .global-dropdown .p-dropdown-panel .p-dropdown-header .p-dropdown-filter:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .global-dropdown .p-dropdown-clear-icon {
          color: #94a3b8;
          right: 40px;
        }

        .global-dropdown .p-dropdown-clear-icon:hover {
          color: #ef4444;
        }

        .global-dropdown.p-disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};